import { useEffect, useMemo, useState } from 'react';
import { Download, Gift, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  giftVouchersApi,
  type GiftVoucherPurchaseDto,
  type GiftVoucherStatsDto,
} from '../../../lib/api/gift-vouchers';

const fmtEuroFromCents = (cents: number): string =>
  (cents / 100).toFixed(2).replace('.', ',') + ' €';

const THEME_LABELS: Record<string, string> = {
  weihnachten: 'Weihnachten',
  geburtstag: 'Geburtstag',
  valentinstag: 'Valentinstag',
  neutral: 'Neutral',
};

const STATUS_LABELS: Record<GiftVoucherPurchaseDto['status'], { label: string; className: string }> = {
  pending: { label: 'In Zahlung', className: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30' },
  paid: { label: 'Bezahlt', className: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
  failed: { label: 'Fehlgeschlagen', className: 'bg-red-500/10 text-red-300 border-red-500/30' },
  refunded: { label: 'Erstattet', className: 'bg-gray-500/10 text-gray-300 border-gray-500/30' },
  cancelled: { label: 'Abgebrochen', className: 'bg-gray-500/10 text-gray-300 border-gray-500/30' },
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso.replace(' ', 'T')).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export default function PurchasedGiftVouchersPage() {
  const [items, setItems] = useState<GiftVoucherPurchaseDto[]>([]);
  const [stats, setStats] = useState<GiftVoucherStatsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | GiftVoucherPurchaseDto['status']>('all');

  const load = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([giftVouchersApi.list(), giftVouchersApi.stats()]);
      setItems(list);
      setStats(s);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(
    () => (statusFilter === 'all' ? items : items.filter((i) => i.status === statusFilter)),
    [items, statusFilter]
  );

  const redemptionRate = useMemo(() => {
    if (!stats || stats.paidCount === 0) return 0;
    return (stats.redeemedCount / stats.paidCount) * 100;
  }, [stats]);

  const handleResend = async (id: number) => {
    setResendingId(id);
    try {
      await giftVouchersApi.resend(id);
      toast.success('Gutschein-Mail erneut versendet');
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Versand fehlgeschlagen');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Verkaufte Geschenkgutscheine</h1>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Neu laden
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Verkäufe</CardDescription>
              <CardTitle className="text-2xl">{stats.paidCount}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              {stats.totalCount} inkl. pending/failed
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Brutto-Umsatz</CardDescription>
              <CardTitle className="text-2xl">{fmtEuroFromCents(stats.paidAmountCents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Erstattet: {fmtEuroFromCents(stats.refundedAmountCents)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Eingelöste Gutscheine</CardDescription>
              <CardTitle className="text-2xl">
                {stats.redeemedCount}{' '}
                <span className="text-sm text-muted-foreground">/ {stats.paidCount}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              {redemptionRate.toFixed(1)} %
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Offener Wert</CardDescription>
              <CardTitle className="text-2xl">{fmtEuroFromCents(stats.outstandingAmountCents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">Noch nicht eingelöst</CardContent>
          </Card>
        </div>
      )}

      {/* Breakdowns */}
      {stats && (stats.byTheme.length > 0 || stats.byStandort.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verteilung nach Theme</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.byTheme.length === 0 ? (
                <div className="text-sm text-muted-foreground">Noch keine Verkäufe.</div>
              ) : (
                stats.byTheme.map((r) => (
                  <ThemeBar
                    key={r.theme}
                    label={THEME_LABELS[r.theme] || r.theme}
                    count={r.count}
                    amountCents={r.amountCents}
                    max={Math.max(...stats.byTheme.map((x) => x.amountCents))}
                  />
                ))
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verteilung nach Standort</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.byStandort.length === 0 ? (
                <div className="text-sm text-muted-foreground">Noch keine Verkäufe.</div>
              ) : (
                stats.byStandort.map((r) => (
                  <ThemeBar
                    key={r.standortId ?? 'none'}
                    label={r.standortName || '(Kein Standort)'}
                    count={r.count}
                    amountCents={r.amountCents}
                    max={Math.max(...stats.byStandort.map((x) => x.amountCents))}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'paid', 'pending', 'failed', 'refunded'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              statusFilter === s
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40'
            }`}
          >
            {s === 'all' ? 'Alle' : STATUS_LABELS[s as GiftVoucherPurchaseDto['status']].label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Einzelverkäufe</CardTitle>
          <CardDescription>
            {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Erstellt</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Code</th>
                <th className="py-2 pr-3 font-medium">Betrag</th>
                <th className="py-2 pr-3 font-medium">Käufer</th>
                <th className="py-2 pr-3 font-medium">Empfänger</th>
                <th className="py-2 pr-3 font-medium">Versand</th>
                <th className="py-2 pr-3 font-medium">Standort</th>
                <th className="py-2 pr-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-muted-foreground">
                    Keine Verkäufe.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const isRedeemed = r.redemptionCount > 0;
                const badge = STATUS_LABELS[r.status];
                return (
                  <tr key={r.id} className="border-b border-border/50 align-top">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded border ${badge.className}`}>
                        {badge.label}
                      </span>
                      {isRedeemed && (
                        <span className="ml-1 inline-flex items-center text-xs px-2 py-0.5 rounded border bg-primary/10 text-primary border-primary/30">
                          eingelöst
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.couponCode || '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtEuroFromCents(r.amountCents)}</td>
                    <td className="py-2 pr-3">
                      <div>{r.buyerName}</div>
                      <div className="text-xs text-muted-foreground">{r.buyerEmail}</div>
                    </td>
                    <td className="py-2 pr-3">
                      {r.deliveryMode === 'recipient' ? (
                        <>
                          <div>{r.recipientName}</div>
                          <div className="text-xs text-muted-foreground">{r.recipientEmail}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">An Käufer</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {r.sentAt ? (
                        <span className="text-emerald-400">Gesendet {fmtDate(r.sentAt)}</span>
                      ) : r.scheduledSendAt ? (
                        <span className="text-yellow-400">Geplant {fmtDate(r.scheduledSendAt)}</span>
                      ) : r.status === 'paid' ? (
                        <span className="text-muted-foreground">Ausstehend</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {r.lastSendError && (
                        <div className="text-[10px] text-red-400 mt-0.5" title={r.lastSendError}>
                          Fehler: {r.lastSendError.slice(0, 40)}…
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">{r.standortName || '—'}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {r.status === 'paid' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 mr-1"
                            onClick={() => giftVouchersApi.openPdf(r.id)}
                            title="PDF öffnen"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            disabled={resendingId === r.id}
                            onClick={() => handleResend(r.id)}
                            title="Mail erneut senden"
                          >
                            <Mail className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ThemeBar({
  label,
  count,
  amountCents,
  max,
}: {
  label: string;
  count: number;
  amountCents: number;
  max: number;
}) {
  const pct = max > 0 ? (amountCents / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span>{label}</span>
        <span>
          {count} × · {fmtEuroFromCents(amountCents)}
        </span>
      </div>
      <div className="h-2 rounded bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
